import { ReactNode } from 'react';
import styled from 'styled-components';
import { RainbowBackgroundAnimation } from '@/base/GlueRainbowBackgroundAnimation';
import { ArrowIcon } from '@/base/ArrowIcon';
import { NodeEventInsulator } from '@/GraphEditor/drawingLayer/graphComponents/NodeInterfaceEventInsulator';
import { useRerenderOnElementResize } from '@/GraphEditor/drawingLayer/helpers/useRerenderOnElementResize';
import { useTriggerRun } from '@/GraphEditor/drawingLayer/contexts/TriggerRunContext';
import { useNodeSelected } from '@/GraphEditor/drawingLayer/contexts/NodeSelectedContext';

const NodeBody = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
`;

const NodeHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    padding-top: 8px;
    padding-bottom: 12px;
`;

const NodeTitle = styled.h2`
    margin: 0;
    font-weight: 500;
    font-size: 1.2em;
    flex-grow: 1;
    text-align: left;
    padding-left: 8px;
    padding-right: 8px;
`;

const RunNodeButton = styled.button`
    position: absolute;
    top: -40px;
    left: 4px;

    &:hover {
        cursor: pointer;
        opacity: 1;
        ${RainbowBackgroundAnimation}
    }

    opacity: 0.5;
    transition: opacity 0.3s ease;

    box-sizing: border-box;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${props => props.theme.backgroundColor};
    border: 2px solid ${props => props.theme.separatorColor};
`;

interface Props {
    name: string;
    renderOutPorts: () => ReactNode;
    renderInPorts: () => ReactNode;

    renderInherentInPort: () => ReactNode;
    renderInherentOutPort: () => ReactNode;

    renderIntegrationMarker?: () => ReactNode;
    renderSpecialMarker?: () => ReactNode;
    children?: ReactNode;
}

export const NodeContentStandardLayout: React.FC<Props> = ({
    name,
    renderInherentInPort,
    renderInherentOutPort,
    renderOutPorts,
    renderInPorts,
    renderIntegrationMarker,
    renderSpecialMarker,
    children,
}) => {
    const triggerRun = useTriggerRun();
    const selected = useNodeSelected();

    const renderManualTrigger = () => {
        if (selected) {
            return (
                <RunNodeButton aria-label={`Run block ${name}`} onClick={triggerRun}>
                    <ArrowIcon />
                </RunNodeButton>
            );
        }
        return null;
    };

    const { elementRef: nodeInterfaceContainerRef } = useRerenderOnElementResize();

    return (
        <>
            <NodeHeader>
                {renderInherentInPort()}
                <NodeTitle>{name}</NodeTitle>
                {renderInherentOutPort()}
            </NodeHeader>
            {renderIntegrationMarker?.()}
            {renderSpecialMarker?.()}
            <NodeBody>
                {renderInPorts()}
                <NodeEventInsulator>
                    <div ref={nodeInterfaceContainerRef}>
                        {children}
                        {renderManualTrigger()}
                    </div>
                </NodeEventInsulator>
                {renderOutPorts()}
            </NodeBody>
        </>
    );
};
